import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import { cstDateTime, cstTime } from '../lib/time';
import { Archive, MessageSquare, X, Star, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function Log() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [last, setLast] = useState(null);
  const [loading, setLoading] = useState(false);
  // Iter 84 — inline rename state: {id, value} while an operator is
  // editing a log title. Only one row can be renamed at a time.
  const [renaming, setRenaming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/chat/archive');
      setRows(data.archives || []);
      if (!selected && (data.archives || []).length) setSelected(data.archives[0]);
      setLast(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  };

  const remove = async (row, e) => {
    e.stopPropagation();
    if (!row?.id) return;
    if (!window.confirm(`Delete saved chat "${row.title || '(untitled)'}"?`)) return;
    try {
      const { data } = await api.delete(`/chat/archive/${row.id}`);
      if (data?.deleted) {
        toast.success('Saved chat removed');
        setRows((rs) => rs.filter((r) => r.id !== row.id));
        if (selected?.id === row.id) setSelected(null);
      } else {
        toast.error('Already removed');
      }
    } catch { toast.error('Delete failed'); }
  };

  const toggleStar = async (row, e) => {
    e.stopPropagation();
    if (!row?.id) return;
    try {
      const { data } = await api.patch(`/chat/archive/${row.id}/star`);
      if (data?.ok) {
        setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, starred: data.starred } : r));
        toast.success(data.starred ? 'Log starred' : 'Star removed');
      }
    } catch { toast.error('Star toggle failed'); }
  };

  const beginRename = (row, e) => {
    e.stopPropagation();
    if (!row?.id) return;
    setRenaming({ id: row.id, value: row.title || '' });
  };

  const cancelRename = (e) => {
    if (e) e.stopPropagation();
    setRenaming(null);
  };

  const saveRename = async (row, e) => {
    if (e) e.stopPropagation();
    if (!renaming || !row?.id || renaming.id !== row.id) return;
    const nextTitle = (renaming.value || '').trim();
    if (!nextTitle) {
      toast.error('Title cannot be empty');
      return;
    }
    if (nextTitle === row.title) {
      setRenaming(null);
      return;
    }
    try {
      const { data } = await api.patch(`/chat/archive/${row.id}/rename`, { title: nextTitle });
      if (data?.ok) {
        setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, title: data.title } : r));
        if (selected?.id === row.id) setSelected((s) => ({ ...s, title: data.title }));
        toast.success('Log renamed');
        setRenaming(null);
      } else {
        toast.error(data?.error === 'forbidden' ? 'Not your log' : 'Rename failed');
      }
    } catch { toast.error('Rename failed'); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-display tracking-[0.35em] uppercase text-cyan-sys text-sm flex items-center gap-2">
          <Archive size={14} /> conversation log // saved briefings
        </div>
        <RefreshButton onRefresh={load} lastUpdated={last} testId="log-refresh" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-3">
        <CornerPanel title={`archived · ${rows.length}`} testId="log-list">
          {loading && rows.length === 0 && (
            <div className="text-[var(--txt-muted)] text-[12px]">// loading...</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="text-[var(--txt-muted)] text-[12px] space-y-2">
              <div>// no archived conversations yet.</div>
              <div className="text-[10px]">
                Open the ATDE-Assist chat widget, click the <span className="text-cyan-sys">save</span> icon, and your active session will land here.
              </div>
            </div>
          )}
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1" data-testid="log-archive-scroll">
            {rows.map((r) => {
              const isEditing = renaming && renaming.id === r.id;
              return (
              <div
                key={r.session_id + r.archived_at}
                className="relative group"
              >
                <button
                  data-testid={`log-item-${r.session_id}`}
                  onClick={() => !isEditing && setSelected(r)}
                  className={`w-full text-left p-2 pr-20 border transition-colors ${
                    selected && selected.archived_at === r.archived_at
                      ? 'border-cyan-sys bg-[#0d1113] text-cyan-sys'
                      : `${r.starred ? 'border-hold/40' : 'border-[var(--border-default)]'} hover:border-cyan-sys text-[var(--txt-secondary)]`
                  }`}
                >
                  <div className="font-mono text-[11px] truncate flex items-center gap-1.5">
                    {r.starred && <Star size={11} className="fill-hold text-hold shrink-0" data-testid={`log-star-badge-${r.session_id}`} />}
                    {isEditing ? (
                      <input
                        data-testid={`log-rename-input-${r.session_id}`}
                        autoFocus
                        value={renaming.value}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenaming({ id: r.id, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveRename(r, e); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename(e); }
                        }}
                        maxLength={120}
                        className="flex-1 bg-black border border-cyan-sys text-cyan-sys outline-none px-1 py-0.5 font-mono text-[11px]"
                      />
                    ) : (
                      <span className="truncate">{r.title || '(no title)'}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[9px] tracking-widest uppercase text-[var(--txt-muted)]">
                    <span>{r.messages?.length || 0} msgs</span>
                    <span>{cstDateTime(r.archived_at)}</span>
                  </div>
                </button>
                {isEditing ? (
                  <>
                    <button
                      data-testid={`log-rename-save-${r.session_id}`}
                      onClick={(e) => saveRename(r, e)}
                      aria-label="save rename"
                      title="save (enter)"
                      className="absolute top-1.5 right-7 text-cyan-sys hover:text-buy"
                    >
                      <Check size={12} />
                    </button>
                    <button
                      data-testid={`log-rename-cancel-${r.session_id}`}
                      onClick={cancelRename}
                      aria-label="cancel rename"
                      title="cancel (esc)"
                      className="absolute top-1.5 right-1.5 text-[var(--txt-muted)] hover:text-sell"
                    >
                      <X size={12} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      data-testid={`log-rename-${r.session_id}`}
                      onClick={(e) => beginRename(r, e)}
                      aria-label="rename log"
                      title="rename log"
                      className="absolute top-1.5 right-14 text-[var(--txt-muted)] hover:text-cyan-sys opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      data-testid={`log-star-${r.session_id}`}
                      onClick={(e) => toggleStar(r, e)}
                      aria-label={r.starred ? 'unstar log' : 'star log'}
                      className={`absolute top-1.5 right-7 transition-colors ${r.starred ? 'text-hold' : 'text-[var(--txt-muted)] hover:text-hold opacity-0 group-hover:opacity-100'}`}
                      title={r.starred ? 'starred — click to remove' : 'star this log'}
                    >
                      <Star size={12} className={r.starred ? 'fill-hold' : ''} />
                    </button>
                    <button
                      data-testid={`log-delete-${r.session_id}`}
                      onClick={(e) => remove(r, e)}
                      aria-label="delete saved chat"
                      className="absolute top-1.5 right-1.5 text-[var(--txt-muted)] hover:text-sell opacity-0 group-hover:opacity-100 transition-opacity"
                      title="delete saved chat"
                    >
                      <X size={12} />
                    </button>
                  </>
                )}
              </div>
              );
            })}
          </div>
        </CornerPanel>

        <CornerPanel
          title={selected ? selected.title || 'conversation' : 'select a conversation'}
          testId="log-detail"
          right={selected && (
            <span className="text-[var(--txt-muted)]">
              {selected.messages?.length || 0} messages · {cstDateTime(selected.archived_at)}
            </span>
          )}
        >
          {!selected && (
            <div className="text-[var(--txt-muted)] text-[12px] flex items-center gap-2">
              <MessageSquare size={12} /> pick a conversation from the left.
            </div>
          )}
          {selected && (
            <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-3 font-mono text-[12px]">
              {(selected.messages || []).map((m, i) => (
                <div key={i} className="whitespace-pre-wrap">
                  {m.role === 'user' ? (
                    <>
                      <div className="text-hold text-[10px] tracking-widest uppercase mb-0.5 flex items-center justify-between">
                        <span>// operator</span>
                        {m.t && <span className="text-[var(--txt-muted)] normal-case tracking-normal">{cstTime(m.t)}</span>}
                      </div>
                      <div className="text-[var(--txt-primary)]">{m.text}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-cyan-sys text-[10px] tracking-widest uppercase mb-0.5 flex items-center justify-between">
                        <span>// atde-assist</span>
                        {m.t && <span className="text-[var(--txt-muted)] normal-case tracking-normal">{cstTime(m.t)}</span>}
                      </div>
                      <div className="text-buy atde-md">
                        <ReactMarkdown>{m.text}</ReactMarkdown>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CornerPanel>
      </div>
    </div>
  );
}
